import { memo, useMemo, useState } from 'react'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, Delete, Pencil } from '@factorialco/f0-react/icons/app'
import {
  ItManagementDocumentTemplatesListDocument,
  ItManagementDocumentTemplatesListQuery,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { i18nDateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { DeleteTemplateModal } from 'modules/it_management/components/DocumentTemplates/DeleteTemplateModal'
import { TemplateFormModal } from 'modules/it_management/components/DocumentTemplates/TemplateFormModal'

type Template =
  ItManagementDocumentTemplatesListQuery['itManagement']['documentTemplatesConnection']['nodes'][number]

const DocumentTemplatesPage = () => {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [templateToEdit, setTemplateToEdit] = useState<Template | null>(null)
  const [templateToDelete, setTemplateToDelete] = useState<Template | null>(null)

  const dataSourceOptions = useMemo(() => {
    return {
      document: ItManagementDocumentTemplatesListDocument,
      mapData: (data: ItManagementDocumentTemplatesListQuery) => {
        return data.itManagement.documentTemplatesConnection.nodes.map((template) => ({
          ...template,
          // Add grouping key if needed
          _groupKey: template.templateType,
        }))
      },
      sortings: {
        createdAt: {
          label: i18n.t('it_management.common.created_at'),
          sortBy: 'createdAt',
        },
        title: {
          label: i18n.t('it_management.settings.document_templates.columns.title'),
          sortBy: 'title',
        },
      },
      defaultSorting: {
        field: 'createdAt' as const,
        order: 'desc' as const,
      },
      filters: {
        templateType: {
          type: 'in' as const,
          label: i18n.t('it_management.settings.document_templates.columns.type'),
          options: {
            options: async () => [
              {
                label: i18n.t('it_management.settings.document_templates.types.assign'),
                value: 'assign',
              },
              {
                label: i18n.t('it_management.settings.document_templates.types.unassign'),
                value: 'unassign',
              },
            ],
          },
        },
      },
      primaryActions: () => ({
        label: i18n.t('it_management.settings.document_templates.actions.new_template'),
        icon: Add,
        onClick: () => setShowCreateModal(true),
      }),
      itemActions: (item: Template) => [
        {
          label: i18n.t('it_management.settings.document_templates.actions.edit'),
          icon: Pencil,
          onClick: () => setTemplateToEdit(item),
        },
        {
          label: i18n.t('it_management.settings.document_templates.actions.delete'),
          icon: Delete,
          onClick: () => setTemplateToDelete(item),
          critical: true,
        },
      ],
      mapVariables: (variables: {
        sortings?: Array<{ field: string; order: string }>
        filters?: { templateType?: string[] }
      }) => ({
        templateTypes: variables.filters?.templateType,
      }),
      mapPagination: (data: ItManagementDocumentTemplatesListQuery) => ({
        total: data.itManagement.documentTemplatesConnection.totalCount ?? 0,
      }),
    }
  }, [])

  const source = useGraphqlDataCollectionSource(dataSourceOptions, [])

  const translateTemplateType = (type: string) => {
    return type === 'assign'
      ? i18n.t('it_management.settings.document_templates.types.assign')
      : i18n.t('it_management.settings.document_templates.types.unassign')
  }

  return (
    <>
      <OneDataCollection
        source={source}
        emptyStates={{
          'no-data': {
            emoji: '📄',
            title: i18n.t('it_management.settings.document_templates.empty_state.title'),
            description: i18n.t(
              'it_management.settings.document_templates.empty_state.description'
            ),
          },
        }}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('it_management.settings.document_templates.columns.title'),
                  render: (item: Template) => item.title,
                  sorting: 'title',
                },
                {
                  label: i18n.t('it_management.settings.document_templates.columns.type'),
                  render: (item: Template) => ({
                    type: 'status',
                    value: {
                      status: 'neutral',
                      label: translateTemplateType(item.templateType),
                    },
                  }),
                },
                {
                  label: i18n.t('it_management.settings.document_templates.columns.languages'),
                  render: (item: Template) => ({
                    type: 'text',
                    value: item.twoLanguages
                      ? i18n.t('it_management.settings.document_templates.languages.two')
                      : i18n.t('it_management.settings.document_templates.languages.one'),
                  }),
                },
                {
                  label: i18n.t('it_management.common.created_at'),
                  render: (item: Template) => ({
                    type: 'text',
                    value: i18nDateToString(moment(item.createdAt), 'ui_short_with_time'),
                  }),
                  sorting: 'createdAt',
                },
              ],
            },
          },
        ]}
      />

      {showCreateModal && <TemplateFormModal onClose={() => setShowCreateModal(false)} />}
      {templateToEdit && (
        <TemplateFormModal template={templateToEdit} onClose={() => setTemplateToEdit(null)} />
      )}
      {templateToDelete && (
        <DeleteTemplateModal
          template={templateToDelete}
          onClose={() => setTemplateToDelete(null)}
        />
      )}
    </>
  )
}

export default memo(DocumentTemplatesPage)
